import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { cx } from './cx';
import { useAnchoredStyle, useOutsidePress } from './popover';

export interface SelectOption<V extends string = string> {
  value: V;
  label: string;
  /** 12px sub-line under the label. */
  sub?: string;
  /** Small chip on the right, e.g. "Oldest", "On leave", "Away". */
  tag?: string;
  disabled?: boolean;
}

export interface SelectProps<V extends string = string> {
  value: V | null | undefined;
  options: SelectOption<V>[];
  onChange: (value: V) => void;
  placeholder?: string;
  size?: 'md' | 'sm';
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  /** Visible label element's id; or use aria-label. */
  'aria-labelledby'?: string;
  'aria-label'?: string;
  /** Optional content rendered before the label in the trigger. */
  leading?: ReactNode;
}

const TYPEAHEAD_RESET_MS = 600;

/**
 * Select-only combobox (WAI-ARIA APG pattern): focus stays on the trigger and
 * the highlighted option is announced through aria-activedescendant.
 * ↑/↓ move, Home/End jump, Enter/Space pick, Esc closes, printable keys
 * type-ahead. Disabled options are skipped by the keyboard and ignore clicks.
 */
export function Select<V extends string = string>({
  value,
  options,
  onChange,
  placeholder = 'Select',
  size = 'md',
  id,
  disabled,
  invalid,
  className,
  leading,
  ...aria
}: SelectProps<V>) {
  const autoId = useId().replace(/:/g, '');
  const baseId = id || `sel-${autoId}`;
  const listId = `${baseId}-listbox`;
  const optId = (i: number) => `${baseId}-opt-${i}`;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typeahead = useRef({ text: '', at: 0 });

  const selectedIndex = useMemo(() => options.findIndex((o) => o.value === value), [options, value]);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const sm = size === 'sm';

  const enabledFrom = useCallback(
    (start: number, step: 1 | -1) => {
      for (let i = start; i >= 0 && i < options.length; i += step) {
        if (!options[i].disabled) return i;
      }
      return -1;
    },
    [options],
  );

  const openList = useCallback(
    (focus: 'selected' | 'first' | 'last' = 'selected') => {
      if (disabled) return;
      let idx = -1;
      if (focus === 'selected' && selectedIndex >= 0 && !options[selectedIndex].disabled) idx = selectedIndex;
      else if (focus === 'last') idx = enabledFrom(options.length - 1, -1);
      else idx = enabledFrom(0, 1);
      setActive(idx);
      setOpen(true);
    },
    [disabled, enabledFrom, options, selectedIndex],
  );

  const close = useCallback(() => {
    setOpen(false);
    setActive(-1);
  }, []);

  const pick = useCallback(
    (i: number) => {
      const o = options[i];
      if (!o || o.disabled) return;
      onChange(o.value);
      close();
      triggerRef.current?.focus();
    },
    [options, onChange, close],
  );

  useOutsidePress(open, [triggerRef, listRef], close);
  const panelStyle = useAnchoredStyle(open, triggerRef, { minWidth: 'anchor', maxHeight: 290 });

  const activeId = open && active >= 0 ? optId(active) : undefined;

  // Keep the highlighted option in view.
  useEffect(() => {
    if (activeId) document.getElementById(activeId)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeId]);

  const runTypeahead = (ch: string) => {
    const now = Date.now();
    const t = typeahead.current;
    t.text = now - t.at > TYPEAHEAD_RESET_MS ? ch : t.text + ch;
    t.at = now;
    const q = t.text.toLowerCase();
    const from = open && active >= 0 ? active : selectedIndex;
    // A repeated single letter cycles through matches; otherwise match from the current one.
    const startAt = q.length === 1 ? from + 1 : Math.max(from, 0);
    const n = options.length;
    for (let k = 0; k < n; k++) {
      const i = (startAt + k) % n;
      const o = options[i];
      if (!o.disabled && o.label.toLowerCase().startsWith(q)) {
        if (open) setActive(i);
        else {
          setActive(i);
          setOpen(true);
        }
        return;
      }
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const k = e.key;
    if (!open) {
      if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || k === ' ') {
        e.preventDefault();
        openList(k === 'ArrowUp' && selectedIndex < 0 ? 'last' : 'selected');
      } else if (k === 'Home' || k === 'End') {
        e.preventDefault();
        openList(k === 'Home' ? 'first' : 'last');
      } else if (k.length === 1 && /\S/.test(k) && !e.altKey && !e.ctrlKey && !e.metaKey) {
        runTypeahead(k);
      }
      return;
    }
    switch (k) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = enabledFrom(active + 1, 1);
        if (next >= 0) setActive(next);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = enabledFrom(active - 1, -1);
        if (prev >= 0) setActive(prev);
        break;
      }
      case 'Home':
        e.preventDefault();
        setActive(enabledFrom(0, 1));
        break;
      case 'End':
        e.preventDefault();
        setActive(enabledFrom(options.length - 1, -1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (active >= 0) pick(active);
        else close();
        break;
      case 'Escape':
        e.preventDefault();
        // Don't let the Esc also close an enclosing modal.
        e.stopPropagation();
        close();
        break;
      case 'Tab':
        close();
        break;
      default:
        if (k.length === 1 && /\S/.test(k) && !e.altKey && !e.ctrlKey && !e.metaKey) runTypeahead(k);
    }
  };

  return (
    <div className={cx('relative w-full min-w-0', className)}>
      <button
        ref={triggerRef}
        id={baseId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={activeId}
        aria-invalid={invalid || undefined}
        aria-labelledby={aria['aria-labelledby']}
        aria-label={aria['aria-label']}
        disabled={disabled}
        data-open={open}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onKeyDown}
        className={cx(
          'wp-field flex items-center text-left cursor-pointer hover:bg-fill2 disabled:opacity-50 disabled:cursor-not-allowed',
          sm ? 'h-[34px] px-2.5 gap-2 text-[13px] !rounded-[10px]' : 'h-[46px] px-3.5 gap-2 text-[14.5px]',
        )}
      >
        {leading}
        <span className={cx('flex-1 min-w-0 truncate', !selected && 'text-t5')}>{selected ? selected.label : placeholder}</span>
        <ChevronDown
          size={14}
          strokeWidth={2.2}
          aria-hidden="true"
          className="shrink-0 text-t4 transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </button>
      {open &&
        panelStyle &&
        createPortal(
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-labelledby={aria['aria-labelledby'] || baseId}
            style={{ ...panelStyle, width: 'max-content' }}
            className="wp-pop overflow-auto bg-modal border border-line2 rounded-[14px] p-1.5 shadow-wp"
            onMouseDown={(e) => e.preventDefault()}
          >
            {options.map((o, i) => {
              const isSel = i === selectedIndex;
              const isActive = i === active;
              return (
                <div
                  key={`${o.value}-${i}`}
                  id={optId(i)}
                  role="option"
                  aria-selected={isSel}
                  aria-disabled={o.disabled || undefined}
                  // Named by its label; the sub-line and tag are its description.
                  aria-labelledby={`${optId(i)}-l`}
                  aria-describedby={o.sub || o.tag ? [o.sub && `${optId(i)}-s`, o.tag && `${optId(i)}-t`].filter(Boolean).join(' ') : undefined}
                  onMouseEnter={() => !o.disabled && setActive(i)}
                  onClick={() => pick(i)}
                  className={cx(
                    'flex items-center gap-2.5 w-full text-left px-2.5 py-[9px] rounded-[9px] select-none',
                    o.disabled ? 'text-t5 cursor-not-allowed' : 'text-t1 cursor-pointer',
                    isSel ? 'bg-acc-bg' : isActive && !o.disabled ? 'bg-fill2' : '',
                    isActive && isSel && 'outline outline-1 outline-line2',
                  )}
                >
                  <span className="flex-1 min-w-0">
                    <span id={`${optId(i)}-l`} className="block text-[14px] font-medium">
                      {o.label}
                    </span>
                    {o.sub && (
                      <span id={`${optId(i)}-s`} className="block text-[12px] text-t4 mt-0.5">
                        {o.sub}
                      </span>
                    )}
                  </span>
                  {o.tag && (
                    <span id={`${optId(i)}-t`} className="text-[11px] font-semibold px-2 py-[3px] rounded-full bg-acc-bg text-acc shrink-0">
                      {o.tag}
                    </span>
                  )}
                  <span className="w-3.5 shrink-0 text-acc font-bold text-[13px]" aria-hidden="true">
                    {isSel ? '✓' : ''}
                  </span>
                </div>
              );
            })}
            {options.length === 0 && <div className="px-2.5 py-2 text-[13px] text-t4">No options</div>}
          </div>,
          document.body,
        )}
    </div>
  );
}
